import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import startCase from 'lodash/startCase'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons/faCheckCircle'
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons/faExclamationTriangle'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight'
import { faEdit } from '@fortawesome/pro-regular-svg-icons/faEdit'
import { faEye } from '@fortawesome/pro-regular-svg-icons/faEye'
import { faEyeSlash } from '@fortawesome/pro-regular-svg-icons/faEyeSlash'
import { faTasks } from '@fortawesome/pro-regular-svg-icons/faTasks'

const splitUrl = url => {
    try {
        const { origin, pathname, search, hash } = new URL(url)

        return { origin, path: `${pathname}${search}${hash}` }
    } catch (e) {
        return { origin: url, path: '' }
    }
}

const groupUrls = urls =>
    Object.values(
        urls.reduce((groups, url) => {
            const from = splitUrl(url.from)
            const to = splitUrl(url.to)
            const key = `${url.type}|${from.origin}|${to.origin}`

            if (!groups[key]) {
                groups[key] = { key, type: url.type, from: from.origin, to: to.origin, paths: [] }
            }

            groups[key].paths.push(from.path)

            return groups
        }, {})
    )

const SectionTitle = styled.h3`
    color: #c8c8c8;
    font-size: 0.8rem;
    border-bottom: 1px solid #f0f0f0;
    padding-bottom: 0.5rem;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    font-weight: 500;
`

const UrlList = styled.ul`
    max-height: 10rem;
    overflow: auto;
`

const ContentImportResult = ({ result, type }) => {
    const [showUrls, setShowUrls] = useState(false)
    const { content, relations, urls, importId } = result
    const groups = groupUrls(urls)

    return (
        <>
            <FontAwesomeIcon
                icon={faCheckCircle}
                size="3x"
                className="!block !mb-4 !text-success"
            />
            <p className="!mb-4 overflow-anywhere">
                {startCase(type)} <strong>{content.slug || `#${content.id}`}</strong>{' '}
                {content.created ? 'created' : 'updated'}
            </p>
            <div className="!flex !flex-wrap !justify-center !mb-6">
                <Link
                    className="btn btn-sm btn-outline-primary !m-1"
                    to={`/content/edit/${type}/${content.id}`}
                >
                    <FontAwesomeIcon icon={faEdit} className="!mr-2" />
                    Open in Editor
                </Link>
                {importId && (
                    <Link
                        className="btn btn-sm btn-outline-primary !m-1"
                        to={`/content/import/${importId}?type=${type}`}
                    >
                        <FontAwesomeIcon icon={faTasks} className="!mr-2" />
                        View Asset Copy Progress
                    </Link>
                )}
            </div>

            <div className="!w-full !text-left">
                <SectionTitle>Relations</SectionTitle>
                <table className="table table-sm table-borderless !mb-4">
                    <colgroup>
                        <col style={{ width: '30%' }} />
                        <col />
                    </colgroup>
                    <tbody>
                        {Object.entries(relations).map(([relation, links]) => (
                            <tr key={relation}>
                                <td className="!font-bold !align-top">{startCase(relation)}:</td>
                                <td>
                                    {links === null ? (
                                        <span className="text-grey">Not updated</span>
                                    ) : (
                                        <>
                                            {links.linked.length} linked
                                            {links.missing.length > 0 && (
                                                <div className="!text-danger !break-words">
                                                    <FontAwesomeIcon
                                                        icon={faExclamationTriangle}
                                                        className="!mr-2"
                                                    />
                                                    Not found: {links.missing.join(', ')}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <SectionTitle>Rewritten URLs</SectionTitle>
                {groups.length === 0 ? (
                    <p className="text-grey text-small !mb-0">No URLs needed rewriting.</p>
                ) : (
                    <>
                        <table className="table table-sm table-borderless !mb-0">
                            <colgroup>
                                <col style={{ width: '30%' }} />
                                <col />
                            </colgroup>
                            <tbody>
                                {groups.map(group => (
                                    <tr key={group.key}>
                                        <td className="!font-bold !align-top">
                                            {group.paths.length} {startCase(group.type)}
                                            {group.paths.length === 1 ? '' : 's'}:
                                        </td>
                                        <td className="overflow-anywhere">
                                            {group.from}
                                            <FontAwesomeIcon
                                                icon={faArrowRight}
                                                className="!mx-2"
                                            />
                                            {group.to}
                                            {showUrls && (
                                                <UrlList className="text-grey text-small !pl-4 !mt-1 !mb-0">
                                                    {group.paths.map(path => (
                                                        <li key={path}>{path || '/'}</li>
                                                    ))}
                                                </UrlList>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <button
                            type="button"
                            className="btn btn-link !px-0"
                            onClick={() => setShowUrls(!showUrls)}
                        >
                            <FontAwesomeIcon
                                icon={showUrls ? faEyeSlash : faEye}
                                className="!mr-2"
                            />
                            {showUrls ? 'Hide' : 'Show'} All Paths
                        </button>
                    </>
                )}
            </div>
        </>
    )
}

export default ContentImportResult
